import React, { useCallback } from 'react';
import { Paper, Button, makeStyles, Theme } from '@material-ui/core';
import clsx from 'clsx';
import { BaseEntityField, BaseInfoCompActionsSet, BaseInfoCompProps, BaseInfoCompSaveAction, useModePanelStyle, useModeFormStyle, usePropValue,
    inputStateSetterHelper, FontIcon, ModeInfoCompSelectInputField, ModeInfoCompTextAreaInputField, BaseCompAction, InfoCompPanelHeader,
    ModeInfoCompTextInputField, ModeFormInputSection, ModeInfoCompCheckBoxInputField } from '@moderepo/mode-app-components';



const useStyle = makeStyles((theme: Theme) => {
    return {
        root: {
        },

        result: {
            fontFamily: 'monospace',
            whiteSpace: 'pre',
            overflow  : 'auto',
            width     : '100%',
            resize    : 'vertical',
            height    : 100,
            padding   : theme.spacing(1),
        },
    };
}, {
    name: 'SendDeviceCommandComp', index: 1,
});


/**
 * This is the interface of the set of input fields' values that will be passed to the onClick handler when the 'saveUpdate' button is clicked.
 */
export interface SendDeviceCommandCompInputs {
    readonly predefinedCommand?: string | undefined;
    readonly customCommand?: string | undefined;
    readonly parameters?: string | undefined;
    readonly hasResponse?: boolean | undefined;
}


export interface SendDeviceCommandCompActionsSet extends BaseInfoCompActionsSet {
    readonly formatJson?: BaseCompAction;
    readonly saveUpdate: BaseInfoCompSaveAction<SendDeviceCommandCompInputs>
}


/**
 * These are the props required for the SendDeviceCommandComp
 */
export interface SendDeviceCommandCompProps extends BaseInfoCompProps {
    readonly fields: {
        readonly predefinedCommand?: BaseEntityField<string>;
        readonly customCommand?: BaseEntityField<string>;
        readonly parameters?: BaseEntityField<string>;
        readonly hasResponse?: BaseEntityField<boolean>;
    }

    // The result for the command
    readonly resultData?: string | undefined;

    readonly actions?: SendDeviceCommandCompActionsSet | undefined;

    readonly texts: {
        readonly resultLabel: string;
    };
}


/**
 * This is a dumb component that has the UI for the Send Device Command.
 */
export const SendDeviceCommandComp: React.FC<SendDeviceCommandCompProps> = (props: SendDeviceCommandCompProps) => {
    const [predefinedCommandInput, setPredefinedCommandInput] = usePropValue<string | undefined>(props.fields.predefinedCommand?.value);
    const [customCommandInput, setCustomCommandInput] = usePropValue<string | undefined>(props.fields.customCommand?.value);
    const [parametersInputs, setParametersInput] = usePropValue<string | undefined>(props.fields.parameters?.value);
    const [hasResponseInputs, setHasResponseInput] = usePropValue<boolean | undefined>(props.fields.hasResponse?.value);



    /**
     * On Input changed, update the input state
     */
    const onInputChange = useCallback((inputName: string, value: any) => {
        inputStateSetterHelper(inputName, value, {
            predefinedCommand: setPredefinedCommandInput,
            customCommand    : setCustomCommandInput,
            parameters       : setParametersInput,
            hasResponse      : setHasResponseInput,
        });
    }, [setPredefinedCommandInput, setCustomCommandInput, setParametersInput, setHasResponseInput]);



    const panelClasses = useModePanelStyle();
    const formClasses = useModeFormStyle();
    const classes = useStyle();


    /**
     * On cancel edit, we need to reverse all the input back to the original values
     */
    const reverseFormInput = useCallback(() => {
        setPredefinedCommandInput(props.fields.predefinedCommand?.value);
        setCustomCommandInput(props.fields.customCommand?.value);
        setParametersInput(props.fields.parameters?.value);
        setHasResponseInput(props.fields.hasResponse?.value);
    }, [props.fields.customCommand?.value, props.fields.hasResponse?.value, props.fields.parameters?.value, props.fields.predefinedCommand?.value,
        setCustomCommandInput, setHasResponseInput, setParametersInput, setPredefinedCommandInput]);



    /**
     * On form submit. Collect the values for all input and pass it back to the container
     */
    const onFormSubmit = useCallback(async (event: React.FormEvent) => {
        event.stopPropagation();
        event.preventDefault();

        if (props.actions?.saveUpdate) {
            props.actions.saveUpdate.onClick({
                predefinedCommand: predefinedCommandInput?.trim(),
                customCommand    : customCommandInput?.trim(),
                parameters       : parametersInputs?.trim(),
                hasResponse      : hasResponseInputs,
            });
        }

        return false;
    }, [props.actions?.saveUpdate, predefinedCommandInput, customCommandInput, parametersInputs, hasResponseInputs]);



    return (
        <Paper
            elevation={2}
            className={clsx(panelClasses.root, props.className, classes.root,
                props.showCustomActionOnHover && 'show-custom-action-on-hover')}
        >
            <InfoCompPanelHeader {...props} />
            <div className={clsx(panelClasses.panelContent)}>
                <form
                    className={clsx(formClasses.root, props.formProps?.className)}
                    noValidate
                    autoComplete="on"
                    method="POST"
                    onSubmit={onFormSubmit}
                >
                    <div className={formClasses.formInputGroup}>
                        {props.fields.predefinedCommand && (
                            <ModeInfoCompSelectInputField
                                className={formClasses.formInputField}
                                compProps={props}
                                inputProps={props.fields.predefinedCommand}
                                fieldName="predefinedCommand"
                                inputValue={predefinedCommandInput}
                                onInputChange={onInputChange}
                            />
                        )}
                        {props.fields.customCommand && (
                            <ModeInfoCompTextInputField
                                className={formClasses.formInputField}
                                compProps={props}
                                inputProps={props.fields.customCommand}
                                fieldName="customCommand"
                                inputValue={customCommandInput}
                                onInputChange={onInputChange}
                            />
                        )}
                        {props.fields.parameters && (
                            <ModeInfoCompTextAreaInputField
                                className={formClasses.formInputField}
                                compProps={props}
                                inputProps={props.fields.parameters}
                                fieldName="parameters"
                                rows={5}
                                inputValue={parametersInputs}
                                onInputChange={onInputChange}
                            />
                        )}
                        {props.fields.hasResponse && (
                            <ModeInfoCompCheckBoxInputField
                                className={formClasses.formInputField}
                                compProps={props}
                                inputProps={props.fields.hasResponse}
                                fieldName="hasResponse"
                                inputValue={hasResponseInputs}
                                onInputChange={onInputChange}
                            />
                        )}
                    </div>

                    {props.resultData && (
                        <div className={formClasses.formInputGroup}>
                            <ModeFormInputSection collapsible={false} title={props.texts.resultLabel}>
                                <textarea
                                    className={classes.result}
                                    value={props.resultData}
                                    readOnly
                                />
                            </ModeFormInputSection>
                        </div>
                    )}

                    {props.isEditing && props.actions && (
                        <div className={clsx(formClasses.formActionContainer)}>
                            <div />
                            <div>
                                {props.actions?.formatJson && (
                                    <Button
                                        className={clsx(formClasses.formActionButton)}
                                        color="primary"
                                        size="small"
                                        startIcon={props.actions.formatJson.icon
                                            ? <FontIcon iconName={props.actions.formatJson.icon} />
                                            : undefined}
                                        onClick={() => {
                                            if (props.actions?.formatJson) {
                                                props.actions.formatJson.onClick();
                                            }
                                        }}
                                    >
                                        {props.actions.formatJson.label}
                                    </Button>
                                )}
                                {props.actions?.cancelEdit && (
                                    <Button
                                        className={clsx(formClasses.formActionButton)}
                                        size="small"
                                        startIcon={props.actions.cancelEdit.icon
                                            ? <FontIcon iconName={props.actions.cancelEdit.icon} />
                                            : undefined}
                                        onClick={() => {
                                            reverseFormInput();
                                            if (props.actions?.cancelEdit) {
                                                props.actions.cancelEdit.onClick();
                                            }
                                        }}
                                    >
                                        {props.actions.cancelEdit.label}
                                    </Button>
                                )}
                                {props.actions?.saveUpdate && (
                                    <Button
                                        className={clsx(formClasses.formActionButton)}
                                        variant="contained"
                                        color="primary"
                                        type="submit"
                                        size="small"
                                        startIcon={props.actions.saveUpdate.icon
                                            ? <FontIcon iconName={props.actions.saveUpdate.icon} />
                                            : undefined}
                                    >
                                        {props.actions.saveUpdate.label}
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}
                </form>
            </div>
        </Paper>
    );
};
